import React, { useEffect, useRef, useState } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import LineChart from '../../components/Charts/LineChart';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import { parseMonthToValue, safeNumber } from '../../utils/dataUtils';
import { sortTimePeriods } from '../../utils/exportUtils';
import './EducationMeasures.css';
import html2canvas from "html2canvas";

const EDUCATION_X_START_YYYYMM = 202509

const EducationMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const [chartData8, setChartData8] = useState<any>({ categories: [], series: [] });
    // Reserved for future reuse of the New Hire Education chart.
    // const [chartData9, setChartData9] = useState<any>({ categories: [], series: [] });
    const [clinicalEduData, setClinicalEduData] = useState<any>({ categories: [], series: [] });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setChartData8({ categories: [], series: [] });
            // setChartData9({ categories: [], series: [] });
            setClinicalEduData({ categories: [], series: [] });
            setInitialLoading(false);
            return;
        }

        setLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');

        Promise.all([
            fetch(`/api/education_measures/${hospitalIdsParam}`).then(res => res.ok ? res.json() : null),
            fetch(`/api/pmh_clinical_education/${hospitalIdsParam}`).then(res => res.ok ? res.json() : null)
        ]).then(([eduData, clinicalEduRes]) => {
                if (eduData) {
                    if (isMultiSelect) {
                        processMultiSelectData(eduData);
                    } else {
                        processSingleSelectData(eduData);
                    }
                }
                if (clinicalEduRes) {
                    processClinicalEducationData(clinicalEduRes);
                } else {
                    setClinicalEduData({ categories: [], series: [] });
                }
                setLoading(false);
                setInitialLoading(false);
            })
            .catch(err => {
                console.error("Error fetching education measures:", err);
                setLoading(false);
                setInitialLoading(false);
            });
    }, [selectedHospitalIds, isMultiSelect, refreshKey]);

    const processSingleSelectData = (data: any) => {
        const rows = data?.response?.result?.rows || [];

        const filteredRows = rows.filter((row: any) => {
            const mv = parseMonthToValue(row[2]);
            return mv >= EDUCATION_X_START_YYYYMM;
        }).sort((a: any, b: any) => {
            return parseMonthToValue(a[2]) - parseMonthToValue(b[2]);
        });

        const months = filteredRows.map((row: any) => row[2]);

        const yearlySeries = [
            { name: 'Providers', data: filteredRows.map((r: any) => safeNumber(r[3])) },
            { name: 'Nurses', data: filteredRows.map((r: any) => safeNumber(r[4])) },
            { name: 'Other Staff', data: filteredRows.map((r: any) => safeNumber(r[5])) }
        ];

        /*
        const newHireSeries = [
            { name: 'Providers', data: filteredRows.map((r: any) => safeNumber(r[6])) },
            { name: 'Nurses', data: filteredRows.map((r: any) => safeNumber(r[7])) },
            { name: 'Other Staff', data: filteredRows.map((r: any) => safeNumber(r[8])) }
        ];
        */

        setChartData8({ categories: months, series: yearlySeries });
        // setChartData9({ categories: months, series: newHireSeries });
    };

    /** Same rule as Status at a glance + PMH clinical: average over every selected site; missing month/blank = 0. */
    const processMultiSelectData = (data: any) => {
        try {
            const rows = data?.response?.result?.rows || []
            const columns = data?.response?.result?.column_order || []
            if (rows.length === 0 || selectedHospitalIds.length === 0) return

            const getIdx = (name: string) => columns.indexOf(name)
            const hospitalIdx = getIdx('HospitalId')
            const monthIdx = getIdx('Month')
            const yearlyProvIdx = getIdx('Yearly continued education providers')
            const yearlyNurseIdx = getIdx('Yearly continued education nurses')
            const yearlyOtherIdx = getIdx('Yearly continued education other')
            /*
            const newHireProvIdx = getIdx('New hire education providers')
            const newHireNurseIdx = getIdx('New hire education nurses')
            const newHireOtherIdx = getIdx('New hire education other')
            */

            const yIdx = [yearlyProvIdx, yearlyNurseIdx, yearlyOtherIdx]
            // const nIdx = [newHireProvIdx, newHireNurseIdx, newHireOtherIdx]
            for (let i = 0; i < 3; i++) {
                if (yIdx[i] === -1) yIdx[i] = 3 + i
                // if (nIdx[i] === -1) nIdx[i] = 6 + i
            }

            if (hospitalIdx === -1 || monthIdx === -1) return

            const rowsByMonthAndHosp = new Map<string, Map<string, any>>()
            rows.forEach((row: any[]) => {
                const m = String(row[monthIdx] ?? '').trim()
                if (!m) return
                const hid = String(row[hospitalIdx] ?? '').trim()
                if (!hid) return
                if (!rowsByMonthAndHosp.has(m)) rowsByMonthAndHosp.set(m, new Map())
                rowsByMonthAndHosp.get(m)!.set(hid, row)
            })

            const months = Array.from(rowsByMonthAndHosp.keys())
                .filter((m) => parseMonthToValue(m) >= EDUCATION_X_START_YYYYMM)
                .sort((a, b) => parseMonthToValue(a) - parseMonthToValue(b))

            const siteCount = selectedHospitalIds.length
            const cell = (row: any[] | undefined, col: number) => {
                if (!row || col < 0) return 0
                const v = parseFloat(String(row[col]))
                return Number.isFinite(v) ? v : 0
            }

            const avgOverSites = (month: string, col: number) => {
                const rowMap = rowsByMonthAndHosp.get(month) ?? new Map()
                let sum = 0
                selectedHospitalIds.forEach((hidRaw) => {
                    const hid = String(hidRaw ?? '').trim()
                    sum += cell(rowMap.get(hid), col)
                })
                return Math.round((sum / siteCount) * 100) / 100
            }

            const yearly_continued_providers = months.map((m) => avgOverSites(m, yIdx[0]))
            const yearly_continued_nurses = months.map((m) => avgOverSites(m, yIdx[1]))
            const yearly_continued_other = months.map((m) => avgOverSites(m, yIdx[2]))
            /*
            const new_hire_providers = months.map((m) => avgOverSites(m, nIdx[0]))
            const new_hire_nurses = months.map((m) => avgOverSites(m, nIdx[1]))
            const new_hire_other = months.map((m) => avgOverSites(m, nIdx[2]))
            */
            const yearlySeries = [
                { name: 'Providers', data: yearly_continued_providers },
                { name: 'Nurses', data: yearly_continued_nurses },
                { name: 'Other Staff', data: yearly_continued_other }
            ]

            /*
            const newHireSeries = [
                { name: 'Providers', data: new_hire_providers },
                { name: 'Nurses', data: new_hire_nurses },
                { name: 'Other Staff', data: new_hire_other }
            ]
            */

            setChartData8({ categories: months, series: yearlySeries })
            // setChartData9({ categories: months, series: newHireSeries })
        } catch (error) {
            console.error('Error processing multi-select data', error)
        }
    }

    const processClinicalEducationData = (data: any) => {
        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []

        if (rows.length === 0 || selectedHospitalIds.length === 0) {
            setClinicalEduData({ categories: [], series: [] })
            return
        }

        const getIdx = (name: string) => columns.indexOf(name)
        const timePeriodIdx = getIdx('month')
        const hospitalIdIdx = getIdx('hospital_id')

        const seriesKeys = [
            { name: 'OB Providers', col: 'ob_providers' },
            { name: 'OB Nurses', col: 'ob_nurses' },
            { name: 'ER Providers', col: 'er_providers' },
            { name: 'ER Nurses', col: 'er_nurses' }
        ]

        const rowsByPeriodAndHosp = new Map<string, Map<string, any>>()

        rows.forEach((row: any[]) => {
            const rawPeriod = timePeriodIdx !== -1 ? String(row[timePeriodIdx] ?? '') : ''
            if (!rawPeriod) return
            const period = /baseline/i.test(rawPeriod) ? 'Baseline' : rawPeriod
            const hid = hospitalIdIdx !== -1 ? String(row[hospitalIdIdx] ?? '').trim() : ''
            if (!hid) return
            if (!rowsByPeriodAndHosp.has(period)) rowsByPeriodAndHosp.set(period, new Map())
            rowsByPeriodAndHosp.get(period)!.set(hid, row)
        })

        const sortedPeriods = sortTimePeriods(Array.from(rowsByPeriodAndHosp.keys()), 'monthly')
        const siteCount = selectedHospitalIds.length

        const cellValue = (row: any[] | undefined, colIndex: number) => {
            if (!row || colIndex === -1) return 0
            const n = parseFloat(String(row[colIndex]))
            return Number.isFinite(n) ? n : 0
        }

        const series: { name: string; data: number[] }[] = seriesKeys.map(k => {
            const colIndex = getIdx(k.col)
            const dataPoints = sortedPeriods.map(period => {
                const rowMap = rowsByPeriodAndHosp.get(period) ?? new Map()
                let sum = 0
                selectedHospitalIds.forEach((hidRaw) => {
                    const hid = String(hidRaw ?? '').trim()
                    sum += cellValue(rowMap.get(hid), colIndex)
                })
                return Math.round((sum / siteCount) * 10) / 10
            })
            return { name: k.name, data: dataPoints }
        })

        if (sortedPeriods.length === 0) {
            setClinicalEduData({ categories: [], series: [] })
            return
        }

        setClinicalEduData({ categories: sortedPeriods, series })
    }

    const chart8Ref = useRef<any>(null);
    // const chart9Ref = useRef<any>(null);
    const chart10Ref = useRef<any>(null);
    const chart1containerRef = useRef<HTMLDivElement>(null)
    // const chart2containerRef = useRef<HTMLDivElement>(null)
    const chart3containerRef = useRef<HTMLDivElement>(null)

    if (initialLoading) {
        return <LoadingNotice title="Loading education measures data..." />;
    }

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) menu.style.display = 'none';
    };
   
    const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            }as any);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-menu') || el.classList.contains('export-dropdown')   || el.classList.contains("export-ignore2")
            }as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            let svgTitleElement = '';
            let svgHeight = height;

            if (title) {
                const escapedTitle = title
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');

                svgTitleElement = `<text x="${width / 2}" y="25" text-anchor="middle" class="chart-title">${escapedTitle}</text>`;
                svgHeight = height + 40;
            }

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}">
  <style>
    .chart-title {
      font-family: 'Segoe UI', sans-serif;
      font-size: 16px;
      fill: #1a2a44;
      font-weight: bold;
    }
  </style>
  <rect width="100%" height="100%" fill="white"/>
  ${svgTitleElement}
  <image x="0" y="${title ? 40 : 0}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    return (
        <div id={generatePDFIds.dashboard()} className="education-measures-page">
            <div className="page-header">
                <h1 className="page-title">Education Measures</h1>
                <p className="page-subtitle">Education on the importance of listening to patients, providing respectful care, and addressing implicit bias</p>
            </div>

            <div className="chart-section">
                <div id={generatePDFIds.chart(8)} className="chart-container" ref={chart1containerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">
                            Percentage of Staff Completing Yearly Continued Education
                        </h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-edu-1');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-edu-1" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG(chart1containerRef , undefined , "Percentage of Staff Completing Yearly Continued Education"); closeMenu('export-dropdown-menu-edu-1'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(chart1containerRef,"Percentage of Staff Completing Yearly Continued Education"); closeMenu('export-dropdown-menu-edu-1'); }}>Download PNG</div>
                                <div onClick={() => { chart8Ref.current?.exportToCSV(); closeMenu('export-dropdown-menu-edu-1'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart8Ref}
                        categories={chartData8.categories}
                        series={chartData8.series}
                        goalLine={80}
                        goalLabel="Goal ≥ 80%"
                        markers={false}
                        yAxisLabel="Completion %"
                        colors={["rgb(0,114,178)", "rgb(240,228,66)", "rgb(213,94,0)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Percentage of Staff Completing Yearly Continued Education"
                        chartTitle="Percentage of Staff Completing Yearly Continued Education"
                        pageTitle="Education Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                        includeLegendInExport={true}
                    />
                </div>

                {/*
                <div id={generatePDFIds.chart(9)} className="chart-container" ref={chart2containerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">
                            Percentage of Staff Completing New Hire Education
                        </h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-edu-2');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-edu-2" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG(chart2containerRef, undefined, "Percentage of Staff Completing New Hire Education"); closeMenu('export-dropdown-menu-edu-2'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(chart2containerRef,"Percentage of Staff Completing New Hire Education"); closeMenu('export-dropdown-menu-edu-2'); }}>Download PNG</div>
                                <div onClick={() => { chart9Ref.current?.exportToCSV(); closeMenu('export-dropdown-menu-edu-2'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart9Ref}
                        categories={chartData9.categories}
                        series={chartData9.series}
                        goalLine={80}
                        goalLabel="Goal ≥ 80%"
                        markers={false}
                        yAxisLabel="Completion %"
                        colors={["rgb(0,114,178)", "rgb(240,228,66)", "rgb(213,94,0)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Percentage of Staff Completing New Hire Education"
                        chartTitle="Percentage of Staff Completing New Hire Education"
                        pageTitle="Education Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                        includeLegendInExport={true}
                    />
                </div>
                */}

                <div id={generatePDFIds.chart(10)} className="chart-container" ref={chart3containerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">PMH Clinical Education</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-edu-3');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-edu-3" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG(chart3containerRef, undefined, "PMH Clinical Education"); closeMenu('export-dropdown-menu-edu-3'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(chart3containerRef, "PMH Clinical Education"); closeMenu('export-dropdown-menu-edu-3'); }}>Download PNG</div>
                                <div onClick={() => { chart10Ref.current?.exportToCSV(); closeMenu('export-dropdown-menu-edu-3'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <p className="chart-description">
                        Percent of clinicians caring for pregnant/ postpartum patients with PMH training completed
                    </p>
                    <LineChart
                        ref={chart10Ref}
                        categories={clinicalEduData.categories}
                        series={clinicalEduData.series}
                        height={450}
                        goalLine={80}
                        goalLabel="Goal ≥ 80%"
                        min={0}
                        max={100}
                        tickAmount={10}
                        markers={false}
                        yAxisLabel="Completion %"
                        xAxisLabel="Time Period"
                        colors={["#0072B2", "#D55E00", "#F0E441", "#009E73"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        noDataMessage="No Data Available"
                        exportFilename="PMH_Clinical_Education"
                        chartTitle="PMH Clinical Education"
                        chartSubtitle="Percent of clinicians caring for pregnant/ postpartum patients with PMH training completed"
                        pageTitle="Education Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={1}
                        xAxisLabelFontSize="14px"
                        includeLegendInExport={true}
                    />
                    <p className="data-source">Data from hospital level data form</p>
                </div>
            </div>
        </div>
    );
};

export default EducationMeasures;
